import React, { useContext } from 'react';
import styled from 'astroturf/react';
import { trackClick } from 'js/utils/trackClick';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { CiterightContext } from './CiterightContextProvider';
import { Body } from 'js/design_system/Body';
import { Icon } from 'js/design_system/Icon';
import { Button } from 'js/design_system/Button';
import { Banner } from 'js/design_system/Banner';
import { Menu } from 'js/design_system/Menu';
import { MenuItem } from 'js/design_system/MenuItem';
import { PopoverMode } from 'js/design_system/Popover';
import { useCiterightCitations } from './hooks/useCiterightCitations';
import { exportCitationsAsText, ExportFormat } from './utils/exportCitations';
import { EXPORT_FORMATS } from './constants/exportFormats';
import WorkCard from './components/WorkCard';
import { Spinner } from 'js/design_system/Spinner';

export const SavedReferencesList: React.FC = () => {
  const context = useContext(CiterightContext);
  const {
    generateCitationsForSavedReferences,
    generateBibtexForSavedReferences,
    generateRisForSavedReferences,
    generateModifiedDocxForSavedReferences,
    isLoading,
    error,
    clearError,
  } = useCiterightCitations();

  if (!context) {
    return null;
  }

  const { savedReferences, reportId, report } = context;
  const savedCount = savedReferences.length;
  const hasDocxDocument = report?.document_is_docx === true;

  const getSourceCounts = () => {
    const counts: Record<string, number> = {};
    for (const ref of savedReferences) {
      const src = ref.source || 'unknown';
      counts[src] = (counts[src] || 0) + 1;
    }
    return counts;
  };

  const handleExportCitations = async (format: ExportFormat) => {
    if (savedReferences.length === 0) return;

    trackClick({
      source: 'citeright.references_export',
      entityId: reportId || undefined,
      extra: {
        format: format.toLowerCase(),
        reference_count: savedReferences.length,
        ...getSourceCounts(),
      },
    });

    if (format === 'BibTeX') {
      // BibTeX export handles file download directly
      await generateBibtexForSavedReferences(savedReferences);
    } else if (format === 'RIS') {
      // RIS export handles file download directly
      await generateRisForSavedReferences(savedReferences);
    } else {
      // Other formats use the existing citation generation flow
      const citations =
        await generateCitationsForSavedReferences(savedReferences);
      exportCitationsAsText(citations, format);
    }
  };

  const handleDownloadModifiedDocx = async () => {
    if (savedReferences.length === 0 || !reportId) return;

    trackClick({
      source: 'citeright.download_modified_docx',
      entityId: reportId || undefined,
      extra: {
        reference_count: savedReferences.length,
        ...getSourceCounts(),
      },
    });

    await generateModifiedDocxForSavedReferences(savedReferences, reportId);
  };

  return (
    <Container>
      {savedCount > 0 && (
        <ExportBar>
          {hasDocxDocument && (
            <ImpressionWatcher
              source="citeright.download_references_docx_impression"
              entity_id={reportId || undefined}
            >
              <Button
                size="sm"
                disabled={isLoading}
                onClick={handleDownloadModifiedDocx}
                trackClick={{
                  source: 'citeright.download_modified_docx',
                  entityId: reportId || undefined,
                  extra: {
                    reference_count: savedReferences.length,
                    ...getSourceCounts(),
                  },
                }}
              >
                {isLoading ? (
                  <Spinner size={16} />
                ) : (
                  <Icon name="file_download" size={16} />
                )}
                Download docx with references
              </Button>
            </ImpressionWatcher>
          )}
          <Menu
            mode={PopoverMode.Click}
            placement="bottom-end"
            closeOnClick={true}
            render={() => (
              <div>
                {EXPORT_FORMATS.map(({ format, label }) => (
                  <MenuItem
                    key={format}
                    onClick={
                      isLoading
                        ? undefined
                        : () => handleExportCitations(format)
                    }
                  >
                    {label}
                  </MenuItem>
                ))}
              </div>
            )}
          >
            <Button
              size="sm"
              disabled={isLoading}
              trackClick={{
                source: 'citeright.download_references',
                entityId: reportId || undefined,
                extra: {
                  reference_count: savedReferences.length,
                  ...getSourceCounts(),
                },
              }}
            >
              Download References...
            </Button>
          </Menu>
        </ExportBar>
      )}

      {error && (
        <Banner
          type="danger"
          icon={<Icon name="error_outline" size={16} />}
          actions={[
            {
              content: 'Dismiss',
              onClick: clearError,
            },
          ]}
        >
          {error}
        </Banner>
      )}

      {savedReferences.length === 0 ? (
        <EmptyState>
          <Body size="sm" color="secondary">
            No references saved yet. Add references by clicking "Add to paper"
            on any suggested work.
          </Body>
        </EmptyState>
      ) : (
        <ReferencesList>
          {savedReferences.map((reference, index) => {
            // Generate stable key - prefer work ID, fallback to title hash, then index
            const getStableKey = () => {
              if (reference.work?.id) {
                return `id-${reference.work.id}`;
              }
              if (reference.work?.title) {
                // Simple hash of title for more stable key than index
                const titleHash = reference.work.title
                  .split('')
                  .reduce((acc, char) => acc + char.charCodeAt(0), 0);
                return `title-${titleHash}-${index}`;
              }
              return `fallback-${index}`;
            };

            return (
              <WorkCard
                key={getStableKey()}
                workData={reference}
                showAddToListButton={true}
                showPaperEvaluationChatButton={false}
                pageContext="inline_report"
              />
            );
          })}
        </ReferencesList>
      )}
    </Container>
  );
};

const Container = styled.div``;

const ExportBar = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  padding: 12px 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
`;

const ReferencesList = styled.div`
  max-height: 60vh;
  overflow-y: auto;
`;

export default SavedReferencesList;
